import { useState, useCallback, useEffect } from 'react';
import { X, Bell, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from './PushNotifications';

// Check if banner should be shown (pure function, no side effects)
function shouldShowBanner(isSupported, isSubscribed, permission) {
  if (!isSupported || isSubscribed || permission === 'denied') {
    return false;
  }
  
  const bannerDismissed = localStorage.getItem('push_banner_dismissed');
  const lastDismissed = localStorage.getItem('push_banner_dismissed_at');
  
  if (!bannerDismissed) {
    return true;
  }
  
  if (lastDismissed) {
    const daysSinceDismissed = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24);
    return daysSinceDismissed > 7;
  }
  
  return false;
}

export function PushNotificationBanner() {
  const { isSupported, isSubscribed, permission, subscribe, loading } = usePushNotifications();
  const [manuallyHidden, setManuallyHidden] = useState(false);
  
  // Derive showBanner from props and local state
  const showBanner = !manuallyHidden && shouldShowBanner(isSupported, isSubscribed, permission);

  const handleDismiss = useCallback(() => {
    setManuallyHidden(true);
    localStorage.setItem('push_banner_dismissed', 'true');
    localStorage.setItem('push_banner_dismissed_at', Date.now().toString());
  }, []);

  const handleEnable = useCallback(async () => {
    const success = await subscribe();
    if (success) {
      setManuallyHidden(true);
      localStorage.removeItem('push_banner_dismissed');
    }
  }, [subscribe]);

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-5 duration-300">
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl shadow-2xl p-4 text-white">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Bell className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-lg">Bildirimleri Aç</h4>
            <p className="text-sm text-white/90 mt-1">
              Önemli güncellemeleri, mesajları ve hatırlatıcıları anında alın.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button 
                onClick={handleEnable}
                disabled={loading}
                className="bg-white text-emerald-600 hover:bg-white/90 font-medium"
                size="sm"
              >
                {loading ? 'Etkinleştiriliyor...' : 'Etkinleştir'}
              </Button>
              <Button 
                onClick={handleDismiss}
                variant="ghost"
                className="text-white/80 hover:text-white hover:bg-white/10"
                size="sm"
              >
                Daha Sonra
              </Button>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Device hint */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/20 text-xs text-white/70">
          <Smartphone className="w-4 h-4" />
          <span>iOS ve Android cihazlarda çalışır</span>
        </div>
      </div>
    </div>
  );
}

export default PushNotificationBanner;
